import { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link } from 'react-router-dom';
import { useServerFn } from '@tanstack/react-start';
import { Camera, ArrowLeft } from 'lucide-react';
import { useSession, ProtectedRoute, signOut, authClient } from '@/lib/auth/auth-client';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { useAiAssist } from '@/hooks/use-ai-assist';
import { useFileOpenPref, testPopup } from '@/hooks/use-file-open';
import { usePushNotifications } from '@/lib/push/usePushNotifications';
import { deleteOwnAccount } from '@/lib/account.functions';
import AvailabilitySettings from '@/components/availability/AvailabilitySettings';
import PageGlow from "@/components/PageGlow";
import { GRAD, fg, fgMuted, border, surface, type SharedImage } from './profile/profile-shared';
import ProfileTab from './profile/ProfileTab';
import PermissionsTab from './profile/PermissionsTab';
import MediaTab from './profile/MediaTab';
import DocumentsTab from './profile/DocumentsTab';
import ProfileModals from './profile/ProfileModals';

function ProfilePageInner() {
  const { user, isPending } = useSession();
  const { enabled: aiEnabled, setEnabled: setAiEnabled } = useAiAssist();
  const { newTab, setNewTab } = useFileOpenPref();
  const push = usePushNotifications();
  const runDeleteAccount = useServerFn(deleteOwnAccount);

  // Pop-up test feedback
  const [popupResult, setPopupResult] = useState<'ok' | 'blocked' | null>(null);

  // Account deletion
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting]         = useState(false);
  const [deleteError, setDeleteError]   = useState('');

  // Form state
  const [name,    setName]    = useState('');
  const [bio,     setBio]     = useState('');
  const [phone,   setPhone]   = useState('');
  const [website, setWebsite] = useState('');
  const [age,     setAge]     = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  // Email-change flow (separate from the profile save — it's an auth operation)
  const [email,       setEmail]       = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg,    setEmailMsg]    = useState('');
  const [emailErr,    setEmailErr]    = useState('');

  // UI state
  const [tab,     setTab]     = useState<'profile' | 'permissions' | 'availability' | 'media' | 'documents'>(() => {
    // Restore the tab the user was on so a tab discard returns them to the same
    // section (e.g. Profile → Permissions) instead of the default tab.
    if (typeof window === 'undefined') return 'profile';
    try {
      const saved = window.sessionStorage.getItem('profile-active-tab');
      if (saved === 'profile' || saved === 'permissions' || saved === 'availability' || saved === 'media' || saved === 'documents') {
        return saved;
      }
    } catch { /* ignore */ }
    return 'profile';
  });
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [loaded,  setLoaded]  = useState(false);

  const tabNavRef = useRef<HTMLElement>(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Shared media
  const [convId,  setConvId]  = useState<string | null>(null);
  const [images,  setImages]  = useState<SharedImage[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  // Documents
  const [docs, setDocs] = useState<{ id: string; name: string; url: string; fileType: string; fileSize: string; createdAt: string }[]>([]);

  // Notification channel preferences (email / sms / unread reminders)
  const [notifPrefs, setNotifPrefs] = useState<{ email: boolean; sms: boolean; push: boolean; unreadDigest: boolean }>({ email: true, sms: true, push: true, unreadDigest: true });
  const [notifSaving, setNotifSaving] = useState<'email' | 'sms' | 'unreadDigest' | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  // Key under which in-progress edits are stashed so they survive the mobile
  // browser discarding & restoring the tab (switching apps, etc.). Per-user so
  // drafts never bleed across accounts on a shared device.
  const draftKey = user ? `profile-draft:${user.id}` : null;
  // Becomes true once we've decided what the fields should start as (DB value
  // or restored draft). The save effect waits for this so it never clobbers a
  // saved draft with freshly-loaded DB values during the initial render.
  const draftRestored = useRef(false);

  // Load profile from the database
  useEffect(() => {
    if (!user || loaded) return;
    setEmail(user.email ?? '');
    (async () => {
      const { data: d } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (d) {
        setName(d.name ?? '');
        setBio(d.bio ?? '');
        setPhone(d.phone ?? '');
        setWebsite(d.website ?? '');
        setAge(d.age ? String(d.age) : '');
        setPreview(d.image ?? user.image ?? null);
        const np = (d.notification_prefs ?? {}) as Record<string, unknown>;
        setNotifPrefs({
          email: np.email !== false,
          sms: np.sms !== false,
          push: np.push !== false,
          unreadDigest: np.email_unread_digest !== false,
        });
      } else {
        setName(user.name ?? '');
        setPreview(user.image ?? null);
      }

      // A persisted draft (unsaved edits from before the tab was discarded)
      // wins over the DB values so the user picks up exactly where they left off.
      if (draftKey) {
        try {
          const raw = window.sessionStorage.getItem(draftKey);
          if (raw) {
            const draft = JSON.parse(raw) as Partial<Record<'name' | 'bio' | 'phone' | 'website' | 'age', string>>;
            if (typeof draft.name === 'string') setName(draft.name);
            if (typeof draft.bio === 'string') setBio(draft.bio);
            if (typeof draft.phone === 'string') setPhone(draft.phone);
            if (typeof draft.website === 'string') setWebsite(draft.website);
            if (typeof draft.age === 'string') setAge(draft.age);
          }
        } catch {
          /* ignore malformed draft */
        }
      }

      draftRestored.current = true;
      setLoaded(true);
    })();
  }, [user, loaded, draftKey]);

  // Continuously stash edits so they survive a tab discard. Only runs after the
  // initial values are settled (draftRestored) to avoid saving placeholder data.
  useEffect(() => {
    if (!loaded || !draftKey || !draftRestored.current) return;
    try {
      window.sessionStorage.setItem(draftKey, JSON.stringify({ name, bio, phone, website, age }));
    } catch {
      /* ignore quota / serialization errors */
    }
  }, [loaded, draftKey, name, bio, phone, website, age]);

  // Remember which tab is open so a tab discard returns to the same section.
  useEffect(() => {
    try {
      window.sessionStorage.setItem('profile-active-tab', tab);
    } catch {
      /* ignore */
    }
  }, [tab]);

  // Persist a notification channel preference change (optimistic).
  const saveNotifPref = useCallback(async (patch: Partial<{ email: boolean; sms: boolean; unreadDigest: boolean }>) => {
    if (!user) return;
    const key = ('email' in patch ? 'email' : 'sms' in patch ? 'sms' : 'unreadDigest') as 'email' | 'sms' | 'unreadDigest';
    const next = { ...notifPrefs, ...patch };
    setNotifPrefs(next);
    setNotifSaving(key);
    try {
      await supabase.from('profiles').update({
        notification_prefs: {
          email: next.email,
          sms: next.sms,
          push: next.push,
          email_unread_digest: next.unreadDigest,
        },
      }).eq('id', user.id);
    } finally {
      setNotifSaving(null);
    }
  }, [user, notifPrefs]);

  // Shared media lives in the messaging conversation (delivered in a later phase).
  const loadMedia = useCallback(() => {
    setImages([]);
  }, []);

  useEffect(() => {
    if (tab === 'media') loadMedia();
  }, [tab, loadMedia]);

  // Detect tab-bar overflow for mobile scroll affordances
  useEffect(() => {
    const nav = tabNavRef.current;
    if (!nav) return;
    const check = () => {
      setCanScrollLeft(nav.scrollLeft > 2);
      setCanScrollRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2);
    };
    check();
    nav.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      nav.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);

  // Load documents
  useEffect(() => {
    if (tab !== 'documents' || !user) return;
    (async () => {
      const { data } = await supabase
        .from('client_document')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      setDocs(
        (data ?? []).map((d) => ({
          id: d.id,
          name: d.name,
          url: d.url,
          fileType: d.file_type,
          fileSize: d.file_size,
          createdAt: d.created_at,
        })),
      );
    })();
  }, [tab, user]);

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5 MB.'); return; }
    setAvatarFile(file);
    setPreview(URL.createObjectURL(file));
    setError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    if (!user) return;

    setSaving(true);
    try {
      let imageUrl: string | undefined;
      if (avatarFile) {
        const ext = avatarFile.name.split('.').pop() || 'jpg';
        const path = `${user.id}/avatar-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('avatars')
          .upload(path, avatarFile, { upsert: true, contentType: avatarFile.type });
        if (upErr) throw new Error(upErr.message);
        const { data: signed } = await supabase.storage
          .from('avatars')
          .createSignedUrl(path, 60 * 60 * 24 * 365);
        imageUrl = signed?.signedUrl;
      }

      const { error: updErr } = await supabase
        .from('profiles')
        .update({
          name: name.trim(),
          bio: bio.trim() || null,
          phone: phone.trim() || null,
          website: website.trim() || null,
          age: age ? Number(age) : null,
          ...(imageUrl ? { image: imageUrl } : {}),
        })
        .eq('id', user.id);
      if (updErr) throw new Error(updErr.message);

      setSuccess('Profile updated successfully.');
      setAvatarFile(null);
      // Edits are now persisted to the DB — drop the local draft so it isn't
      // restored over the saved values next time the page loads.
      if (draftKey) {
        try { window.sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
      }
      window.location.reload();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  }

  const emailChanged =
    email.trim().toLowerCase() !== (user?.email ?? '').trim().toLowerCase();

  async function handleUpdateEmail(e: React.FormEvent) {
    e.preventDefault();
    setEmailErr(''); setEmailMsg('');

    const parsed = z
      .string()
      .trim()
      .email({ message: 'Please enter a valid email address.' })
      .max(255, { message: 'Email must be less than 255 characters.' })
      .safeParse(email);
    if (!parsed.success) {
      setEmailErr(parsed.error.issues[0]?.message ?? 'Invalid email address.');
      return;
    }
    if (!emailChanged) {
      setEmailErr('That is already your email address.');
      return;
    }

    setEmailSaving(true);
    try {
      const result = await authClient.updateEmail({ newEmail: parsed.data });
      if (result.error) {
        setEmailErr(result.error.message ?? 'Could not update your email. Please try again.');
      } else {
        setEmailMsg(
          `We've sent a confirmation link to ${parsed.data.toLowerCase()}. Click it to finish changing your email — it won't change until you confirm.`,
        );
      }
    } catch {
      setEmailErr('Something went wrong. Please try again.');
    } finally {
      setEmailSaving(false);
    }
  }

  function handleTestPopup() {
    setPopupResult(testPopup() ? 'ok' : 'blocked');
  }

  async function handleDeleteAccount() {
    if (!user) return;
    const expected = (user.name || user.email || '').trim();
    if (deleteConfirm.trim().toLowerCase() !== expected.toLowerCase()) {
      setDeleteError('That doesn\u2019t match. Type your name exactly to confirm.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await runDeleteAccount({});
      await signOut();
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete your account. Please try again.');
      setDeleting(false);
    }
  }

  if (isPending || !loaded) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2" style={{ borderColor: GRAD }} />
      </div>
    );
  }

  const initials = user?.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? '?';

  const isAdmin = (user as any)?.isAdmin;

  const TABS = [
    { id: 'profile' as const,     label: 'Profile' },
    { id: 'permissions' as const, label: 'Permissions' },
    ...(isAdmin ? [{ id: 'availability' as const, label: 'Availability' }] : []),
    { id: 'media' as const,       label: 'Shared Photos' },
    // Clients have a dedicated Documents page (/dashboard/documents); only admins
    // need the documents view here, so we avoid the confusing duplicate tab.
    ...(isAdmin ? [{ id: 'documents' as const, label: 'Documents' }] : []),
  ];

  return (
    <>
      <Helmet>
        <title>My Profile — Digitally Baffled</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <ProfileModals
        lightbox={lightbox}
        setLightbox={setLightbox}
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        deleteConfirm={deleteConfirm}
        setDeleteConfirm={setDeleteConfirm}
        deleteError={deleteError}
        setDeleteError={setDeleteError}
        deleting={deleting}
        onDeleteAccount={handleDeleteAccount}
        userLabel={user?.name || user?.email || ''}
      />

      <section className="relative overflow-hidden py-6 md:py-10" style={{ background: 'hsl(var(--background))' }}>
        <PageGlow />

        <div className="relative mx-auto max-w-3xl px-6">
          {/* Back link */}
          <Link
            to={isAdmin ? '/admin' : '/dashboard'}
            className="group mb-8 inline-flex items-center gap-1.5 text-sm transition-opacity hover:opacity-70"
            style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-0.5" />
            {isAdmin ? 'Back to Admin' : 'Back to Dashboard'}
          </Link>

          {/* Header */}
          <div className="mb-8 flex items-center gap-5">
            <div className="relative shrink-0">
              {preview ? (
                <img src={preview} alt="Profile" className="h-20 w-20 rounded-full object-cover"
                  style={{ border: `3px solid ${GRAD}` }} />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full text-xl font-bold"
                  style={{ background: GRAD, color: '#ffffff', fontFamily: 'Outfit, sans-serif', border: `3px solid ${GRAD}` }}>
                  {initials}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full shadow-md transition-opacity hover:opacity-80"
                style={{ background: GRAD, color: '#ffffff' }}
                aria-label="Change profile picture">
                <Camera size={13} />
              </button>
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                {user?.name || 'My Profile'}
              </h1>
              <p className="text-sm" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {user?.email}
              </p>
              {bio && (
                <p className="mt-1 max-w-md text-sm leading-relaxed" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {bio}
                </p>
              )}
            </div>
          </div>

          {/* Scrollable underline tab bar */}
          <div className="relative mb-6">
            {canScrollLeft && (
              <div
                className="pointer-events-none absolute left-0 top-0 z-10 h-full w-10 sm:hidden"
                style={{ background: 'linear-gradient(to right, hsl(var(--background)) 0%, transparent 100%)' }}
              />
            )}
            {canScrollRight && (
              <div
                className="pointer-events-none absolute right-0 top-0 z-10 h-full w-10 sm:hidden"
                style={{ background: 'linear-gradient(to left, hsl(var(--background)) 0%, transparent 100%)' }}
              />
            )}
            <nav
              ref={tabNavRef}
              className="flex overflow-x-auto border-b border-white/5 sm:overflow-visible"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`relative shrink-0 whitespace-nowrap px-5 py-4 text-center text-sm transition-colors duration-200 sm:flex-1 sm:px-0 ${
                    tab === t.id
                      ? 'font-bold text-foreground'
                      : 'font-semibold text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className="relative z-10">{t.label}</span>
                  {tab === t.id && (
                    <div
                      className="absolute bottom-0 left-1/2 h-[3px] w-12 -translate-x-1/2 rounded-t-full"
                      style={{ background: GRAD, boxShadow: `0 -4px 12px ${GRAD}99` }}
                    />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* ── Profile tab ── */}
          {tab === 'profile' && (
            <ProfileTab
              fileRef={fileRef}
              onAvatarChange={handleAvatarChange}
              name={name} setName={setName}
              email={email} setEmail={setEmail}
              phone={phone} setPhone={setPhone}
              age={age} setAge={setAge}
              website={website} setWebsite={setWebsite}
              bio={bio} setBio={setBio}
              error={error} setError={setError}
              success={success} setSuccess={setSuccess}
              saving={saving}
              emailErr={emailErr} setEmailErr={setEmailErr}
              emailMsg={emailMsg} setEmailMsg={setEmailMsg}
              emailSaving={emailSaving}
              emailChanged={emailChanged}
              onSubmit={handleSubmit}
              onUpdateEmail={handleUpdateEmail}
              onOpenDelete={() => { setDeleteOpen(true); setDeleteConfirm(''); setDeleteError(''); }}
            />
          )}

          {/* ── Permissions tab ── */}
          {tab === 'permissions' && (
            <PermissionsTab
              isAdmin={isAdmin}
              aiEnabled={aiEnabled}
              setAiEnabled={setAiEnabled}
              push={push}
              notifPrefs={notifPrefs}
              notifSaving={notifSaving}
              saveNotifPref={saveNotifPref}
              newTab={newTab}
              setNewTab={setNewTab}
              popupResult={popupResult}
              setPopupResult={setPopupResult}
              onTestPopup={handleTestPopup}
              phone={phone}
            />
          )}

          {/* ── Availability tab (admins only) ── */}
          {tab === 'availability' && isAdmin && (
            <div className="flex flex-col gap-5">
              <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>Your Availability</h2>
                <p className="mt-1 text-sm" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Set when you're available to clients. They'll see your working hours on their dashboard.
                </p>
              </div>
              <AvailabilitySettings />
            </div>
          )}

          {/* ── Shared Photos tab ── */}
          {tab === 'media' && (
            <MediaTab
              convId={convId}
              images={images}
              mediaLoading={mediaLoading}
              onOpenLightbox={setLightbox}
            />
          )}

          {/* ── Documents tab ── */}
          {tab === 'documents' && (
            <DocumentsTab docs={docs} isAdmin={isAdmin} />
          )}
        </div>
      </section>
    </>
  );
}

export default function ProfilePage() {
  return (
    <ProtectedRoute>
      <ProfilePageInner />
    </ProtectedRoute>
  );
}
